import { Component, OnInit } from "@angular/core";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { Observable } from "rxjs";
import { map, startWith, finalize } from "rxjs/operators";
import { NotificationService } from "src/app/core/services/Notification/notification.service";
import { SuperAdminService } from "src/app/super-admin.service";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-ourown-vehicle-expense",
  templateUrl: "./ourown-vehicle-expense.component.html",
  styleUrls: ["./ourown-vehicle-expense.component.css"],
})
export class OurownVehicleExpenseComponent implements OnInit {
  isEditMode = false;
  expenseId!: string;
  vehicles: { _id: string; vehicle_no: string; type: string }[] = [];
  selectedVehicleId: string | null = null;
  filteredVehicles!: Observable<
    { _id: string; vehicle_no: string; type: string }[]
  >;
  filesToUpload: File[] = [];
  existingAttachments: string[] = [];
  removedAttachments: string[] = [];
  apiUrl = environment.domain;

  addOwnVicheleMaintenanceForm = new FormGroup({
    vehicle: new FormControl<any>(null, Validators.required),
    vehicleType: new FormControl({ value: "", disabled: true }),
    location: new FormControl(""),
    expenseDate: new FormControl("", Validators.required),
    driverAdvance: new FormControl(""),
    loaderAdvance: new FormControl(""),
    startMeterReading: new FormControl(""),
    endMeterReading: new FormControl(""),
    totalKms: new FormControl({ value: 0, disabled: true }),
    policeAmount: new FormControl(null),
    fuelExpense: new FormControl(null),
    totalAmount: new FormControl({ value: 0, disabled: true }),
    other: new FormControl(null),
    otherDescription: new FormControl(""),
    // billAttachment: new FormControl(""),
  });

  constructor(
    private superAdminService: SuperAdminService,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private notification: NotificationService,
  ) {}

  ngOnInit(): void {
    this.getOwnVehicles();

    this.expenseId = this.route.snapshot.paramMap.get("id") as string;
    this.addOwnVicheleMaintenanceForm
      .get("vehicle")!
      .valueChanges.subscribe((vehicle: any) => {
        if (vehicle) {
          this.addOwnVicheleMaintenanceForm.patchValue({
            vehicleType: vehicle.type || "",
          });
        } else {
          this.addOwnVicheleMaintenanceForm.patchValue({
            vehicleType: "",
          });
        }
      });
    this.addOwnVicheleMaintenanceForm.valueChanges.subscribe(() => {
      this.calculateTotalKms();
      this.calculateTotalAmount();
    });

    if (this.expenseId) {
      this.isEditMode = true;
      this.getExpenseById(this.expenseId);
    }
  }

  private showValidationErrors() {
    const errors: string[] = [];

    const controls = this.addOwnVicheleMaintenanceForm.controls;

    if (controls.vehicle.invalid) {
      errors.push("Vehicle is required");
    }

    if (controls.expenseDate.invalid) {
      errors.push("Expense date is required");
    }

    // add more if you later add validators

    if (errors.length === 0) {
      errors.push("Please fill all required fields");
    }

    errors.forEach((msg) => {
      this.notification.error(msg);
    });
  }

  getExpenseById(id: string) {
    this.spinner.show();

    this.superAdminService
      .getParticularOwnVehicleExpense(id)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe(
        (res) => {
          if (res.status) {
            const data = res.data;
            this.selectedVehicleId = data.vehicle._id;

            const formattedDate = data.expenseDate
              ? data.expenseDate.split("T")[0]
              : "";

            this.existingAttachments = data.attachments || [];
            this.addOwnVicheleMaintenanceForm.patchValue({
              vehicle: this.vehicles.find((v) => v._id === data.vehicle._id),
              location: data.location,
              expenseDate: formattedDate,
              startMeterReading: data.startMeterReading,
              endMeterReading: data.endMeterReading,
              totalKms: data.totalKms,
              loaderAdvance: data.loaderAdvance,
              policeAmount: data.policeAmount,
              driverAdvance: data.driverAdvance,
              fuelExpense: data.fuelExpense,
              totalAmount: data.totalAmount,
              other: data.other,
              otherDescription: data.otherDescription,
            });
          }
        },
        () => {},
      );
  }

  getOwnVehicles() {
    this.superAdminService.getOwnVehicleDropdown().subscribe((res) => {
      if (res.status) {
        this.vehicles = res.data;

        this.filteredVehicles = this.addOwnVicheleMaintenanceForm
          .get("vehicle")!
          .valueChanges.pipe(
            startWith(""),
            map((value) => this.filterVehicles(value)),
          );

        if (this.isEditMode && this.selectedVehicleId) {
          const selected = this.vehicles.find(
            (v) => v._id === this.selectedVehicleId,
          );

          if (selected) {
            this.addOwnVicheleMaintenanceForm.patchValue({
              vehicle: selected,
              vehicleType: selected.type || "",
            });
          }
        }
      }
    });
  }

  filterVehicles(value: any) {
    const filterValue =
      typeof value === "string"
        ? value.toLowerCase()
        : value?.vehicle_no?.toLowerCase() || "";

    return this.vehicles.filter((v) =>
      v.vehicle_no.toLowerCase().includes(filterValue),
    );
  }

  displayVehicle(vehicle: any): string {
    return vehicle ? vehicle.vehicle_no : "";
  }

  removeAttachment(file: string) {
    this.removedAttachments.push(file);
    this.existingAttachments = this.existingAttachments.filter(
      (f) => f !== file,
    );
  }

  removeExistingAttachment(file: string) {
    this.removedAttachments.push(file);
    this.existingAttachments = this.existingAttachments.filter(
      (f) => f !== file,
    );
  }

  removeNewAttachment(index: number) {
    this.filesToUpload.splice(index, 1);
  }

  onFileChange(event: any) {
    const files: FileList = event.target.files;

    for (let i = 0; i < files.length; i++) {
      const exists = this.filesToUpload.some(
        (f) => f.name === files[i].name && f.size === files[i].size,
      );

      if (!exists) {
        this.filesToUpload.push(files[i]);
      }
    }

    // Reset input so same file can be reselected
    event.target.value = "";
  }

  fileChangeEventattachReceipt1(event: any) {
    this.filesToUpload.push(event.target.files[0]);
  }

  submitForm() {
    if (this.addOwnVicheleMaintenanceForm.invalid) {
      this.addOwnVicheleMaintenanceForm.markAllAsTouched();
      this.showValidationErrors();
      return;
    }

    const formValue = this.addOwnVicheleMaintenanceForm.getRawValue();

    const formData = new FormData();

    formData.append("vehicle", String(formValue.vehicle?._id || ""));
    formData.append("expenseDate", String(formValue.expenseDate || ""));
    formData.append("location", String(formValue.location || ""));
    formData.append(
      "startMeterReading",
      String(formValue.startMeterReading || 0),
    );
    formData.append("endMeterReading", String(formValue.endMeterReading || 0));
    formData.append("totalKms", String(formValue.totalKms || 0));
    formData.append("driverAdvance", String(formValue.driverAdvance || 0));
    formData.append("loaderAdvance", String(formValue.loaderAdvance || 0));
    formData.append("policeAmount", String(formValue.policeAmount || 0));
    formData.append("fuelExpense", String(formValue.fuelExpense || 0));
    formData.append("other", String(formValue.other || 0));
    formData.append("totalAmount", String(formValue.totalAmount || 0));
    formData.append(
      "otherDescription",
      String(formValue.otherDescription || ""),
    );

    if (this.isEditMode) {
      formData.append("_id", this.expenseId);
      formData.append(
        "removedAttachments",
        JSON.stringify(this.removedAttachments),
      );
    }

    this.filesToUpload.forEach((file) => {
      formData.append("attachment", file);
    });

    this.spinner.show();

    const request$ = this.isEditMode
      ? this.superAdminService.updateOwnVehicleExpense(formData)
      : this.superAdminService.addOwnVehicleExpense(formData);

    request$.pipe(finalize(() => this.spinner.hide())).subscribe((res) => {
      if (res.status) {
        this.router.navigate([
          "superAdmin/ownVehicleExpence/ownVehicleExpenceList",
        ]);
      }
    });
  }

  resetForm() {
    this.addOwnVicheleMaintenanceForm.reset();
  }

  backTo() {
    window.history.back();
  }

  calculateTotalAmount() {
    const form = this.addOwnVicheleMaintenanceForm;

    const total =
      Number(form.get("driverAdvance")?.value || 0) +
      Number(form.get("loaderAdvance")?.value || 0) +
      Number(form.get("policeAmount")?.value || 0) +
      Number(form.get("fuelExpense")?.value || 0) +
      Number(form.get("other")?.value || 0);

    form.patchValue({ totalAmount: total }, { emitEvent: false });
  }

  calculateTotalKms() {
    const start =
      Number(
        this.addOwnVicheleMaintenanceForm.get("startMeterReading")?.value,
      ) || 0;

    const end =
      Number(this.addOwnVicheleMaintenanceForm.get("endMeterReading")?.value) ||
      0;

    this.addOwnVicheleMaintenanceForm.patchValue(
      { totalKms: end >= start ? end - start : 0 },
      { emitEvent: false },
    );
  }
}
